import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { requestByConnection, ResponseDto } from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import PopupCallBackNoAuth from '@r1-frontend/ui-react/components/popups/popupCallback';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { CATEGORIES } from '@r1-frontend/shared/constants/analytics';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import ConnectDomruPopup from '~/src/components/ConnectDomruPopup';
import { ISubmitParams } from '~/src/components/RequestFormPhone';
import { dataLayerForCBPhonePush } from '~/src/helpers/eventsForDataLayer';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';
import { selectCompanyName } from '~/src/store/selectors/city';

import * as ST from './styled';

const multiCheckboxGroup: {
    id?: EProductsId,
    checked: boolean,
    value: string,
}[] = [
        {
            id: undefined, // undefined означает, что это EProductsId.internet + EProductsId.tv и при этом не ломает тип
            checked: false,
            value: 'Интернет+ТВ',
        },
        {
            id: EProductsId.tv,
            checked: false,
            value: 'ТВ',
        },
        {
            id: EProductsId.internet,
            checked: false,
            value: 'Интернет',
        },
        {
            id: EProductsId.videoControl,
            checked: false,
            value: 'Видеонаблюдение',
        },
        {
            id: EProductsId.domofon,
            checked: false,
            value: 'Домофон',
        },
    ];

interface IConnectDomruPopupGroupProps {
    closeBecomeClientPopup: () => void,
    showBecomeClientPopup: boolean,
}

const ConnectDomruPopupGroup = ({ closeBecomeClientPopup, showBecomeClientPopup }: IConnectDomruPopupGroupProps) => {
    const [showRequestFormPopup, setShowRequestFormPopup] = useState(false);
    const [showResponsePopup, setShowResponsePopup] = useState(false);
    const [becomeClientResponseMessage, setBecomeClientResponseMessage] = useState('');
    const [status, setStatus] = useState<EResultPopupStatus>(EResultPopupStatus.ERROR);
    const [filters, setFilters] = useState(multiCheckboxGroup);

    const isMobile = useMatchMediaByWidth(wideBreakpoints.mobile);
    const companyName = useSelector(selectCompanyName);
    const csrf = useSelector(selectCsrf);


    const selectedProductsIds = useMemo(() => {
        const productIdsArray = new Set<EProductsId>([
            EProductsId.internet,
        ]);

        filters
            .filter(el => el.checked)
            .forEach(({ id }) => {
                if (typeof id === 'undefined') {
                    productIdsArray.add(EProductsId.internet);
                    productIdsArray.add(EProductsId.tv);
                } else {
                    productIdsArray.add(id);
                }
            });

        return [...productIdsArray];
    }, [filters]);

    const toggleFilter = useCallback((value) => {
        setFilters(currFilters => currFilters.map(el =>
            el.value === value ? { ...el, checked: !el.checked } : el));
    }, []);

    const sendBecomeClient = useCallback(async(clientData: ISubmitParams) => {
        const resp = await doRequest.apiRequest<ResponseDto>(
            requestByConnection({
                fio: clientData.fio || 'Потенциальный клиент сайта',
                phone: clientData.phone,
                check_call_type: window.location.pathname,
                product_id: selectedProductsIds,
                csrf,
            }),
        );
        if (resp.isSuccess) {
            const { status, message, request_id: requestId = null } = resp.payload;
            const ok = !!status;

            dataLayerForCBPhonePush({
                action: ok ? 'finished' : 'error',
                option: `${selectedProductsIds.join(',')}${ok ? '' : ' | ' + message}`,
                requestId,
                isPopup: true,
            });
            setBecomeClientResponseMessage(message);
            setStatus(ok ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR);
        } else {
            setBecomeClientResponseMessage(resp.error.message);
            setStatus(EResultPopupStatus.ERROR);
            dataLayerForCBPhonePush({
                action: 'error',
                isPopup: true,
                requestId: null,
            });
        }

        setShowRequestFormPopup(false);
        setShowResponsePopup(true);
    }, [selectedProductsIds]);

    const openRequestFormPopup = useCallback(() => {
        setShowRequestFormPopup(true);
    }, []);

    const closeRequestFormPopup = useCallback(() => {
        setShowRequestFormPopup(false);
    }, []);

    const closeResponsePopup = useCallback(() => {
        setShowResponsePopup(false);
    }, []);

    return (
        <>
            <ConnectDomruPopup
                isOpen={showBecomeClientPopup}
                onClose={closeBecomeClientPopup}
                title={`Подключить ${companyName}`}
                showRequestFormPopup={openRequestFormPopup}
                isMobile={isMobile}
            />
            {showRequestFormPopup &&
                <PopupCallBackNoAuth
                    isOpen={showRequestFormPopup}
                    onClose={closeRequestFormPopup}
                    title={'Заявка на подключение'}
                    buttonText={'Подключить'}
                    waitStatus={false}
                    submitRequest={sendBecomeClient}
                    option={`popup | ${selectedProductsIds.join(',')}`}
                    category={CATEGORIES.applicationForm}
                >
                    <ST.SMultiCheckboxWrapper>
                        {filters.map(item => (
                            <ST.SMultiCheckbox
                                key={item.id}
                                value={item.value}
                                onChange={() => toggleFilter(item.value)}
                                checked={item.checked}
                            >
                                {item.value}
                            </ST.SMultiCheckbox>
                        ))}
                    </ST.SMultiCheckboxWrapper>
                </PopupCallBackNoAuth>
            }

            <ST.SPopupResponse
                isOpen={showResponsePopup}
                onClose={closeResponsePopup}
                title={status === EResultPopupStatus.ERROR
                    ? 'Ваша заявка не отправилась'
                    : becomeClientResponseMessage.split('.')[0]}
                text={status === EResultPopupStatus.ERROR
                    ? 'Это какая-то ошибка. Пожалуйста, попробуйте еще раз или напишите нам в чат.'
                    : becomeClientResponseMessage.split('.')[1]}
                type={status}
            />
        </>
    );
};

export default ConnectDomruPopupGroup;

